import React, { useEffect, useState, useCallback } from "react";
import UpdateUserModal from "../../../modals/UpdateUserModal";
import DeleteModal from "../../../modals/DeleteModal";
import userServices from "services/userServices";
import { useNotification } from "providers/NotificationProvider";
import UsersTable from "./UsersTable";
import { Card, Container, Row } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { useTranslation } from "context/TranslationProvider";
import { createUserActionHandlers } from "../../../helpers/userActionsHelper";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";


const UsersPage = () => {
  const [items, setItems] = useState([]);
  const [isItemsLoading, setIsItemsLoading] = useState(true);

  const [tableState, setTableState] = useState({
    page: 1,
    limit: 20,
    sortBy: "createdAt",
    sortOrder: -1,
    filters: {},
    search: "",
  });

  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
  });

  const { t } = useTranslation();
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const { notify } = useNotification();
  const [filterType, setFilterType] = useState("All Users");
  const filteredItems = (filter) => {
    if (filter === "Verified User") return "verified";
    if (filter === "Unverified User") return "unverified";
    return undefined;
  };
  const { user } = useAuth();

  // Update User Modal
  const [updateUserOpen, setUpdateUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [errorKey, setErrorKey] = useState(null);
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const getAllUsers = useCallback(
    async (state = tableState) => {
      setIsItemsLoading(true);
      try {
        const res = await userServices.getAllUsers({
          ...state,
          search: state.search,
          status: filteredItems(filterType),
        });

        const dataObj = res?.data?.data || res?.data || res;
        const users = dataObj?.users || (Array.isArray(dataObj) ? dataObj : []);
        const metaObj = res?.data?.meta || res?.meta || {};

        setItems(users);
        setMeta(metaObj);
      } catch (err) {
        console.error(err);
        setErrorKey("page.users.toast.fetchError");
      } finally {
        setIsItemsLoading(false);
      }
    },
    [filterType, tableState]
  );

  const navigate = useNavigate();

  // Optimized fetch effect with specific dependencies
  useEffect(() => {
    getAllUsers(tableState);
  }, [
    tableState,
    getAllUsers
  ]);

  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === "sort") {
      const newOrder = sortOrder === "asc" ? 1 : -1;
      setTableState((prev) => {
        if (prev.sortBy === sortField && prev.sortOrder === newOrder) {
          return prev;
        }
        return {
          ...prev,
          sortBy: sortField,
          sortOrder: newOrder,
          page: 1,
        };
      });
    }

    if (type === "pagination") {
      setTableState((prev) => {
        if (prev.page === page && prev.limit === sizePerPage) {
          return prev;
        }
        return {
          ...prev,
          page: page,
          limit: sizePerPage,
        };
      });
    }
  };

  useEffect(() => {
    if (errorKey) {
      notify("error", t(errorKey));
      setErrorKey(null);
    }
  }, [errorKey, notify, t]);

  // Delete User
  const deleteRow = (item) => {
    setItemToDelete(item);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setItemToDelete(null);
  };

  const deleteUser = async () => {
    if (!itemToDelete?._id) return;

    setStatusLoadingId(itemToDelete._id);
    try {
      const res = await userServices.deleteUser({
        deletedListingId: itemToDelete._id,
      });

      if (res?.success) {
        notify("success", t(res?.messageKey || "page.users.toast.deleteSuccess", res?.message || t("page.users.toast.deleteSuccess", "User deleted successfully")));
        setItems((prev) =>
          prev.map((i) =>
            i._id === itemToDelete._id ? { ...i, isDeleted: true, accountStatus: "suspended" } : i
          )
        );
      } else {
        notify("error", t(res?.messageKey || res?.error || "page.users.toast.fetchError", res?.message || t("page.users.toast.fetchError", "Failed to fetch user data.")));
      }
    } catch (err) {
      console.error(err);
      notify("error", t("toast.unexpectedError", "Unexpected error occurred."));
    } finally {
      setStatusLoadingId(null);
      handleModalClose();
    }
  };

  // Reset password
  const updatePassword = async (item) => {
    if (!item?.email) return;

    setStatusLoadingId(item._id);
    try {
      const res = await userServices.forgotPasswordEmail(item.email, user._id);
      if (res?.success) {
        notify("success", t(res?.messageKey || "auth.forgot-password.resetLinkMessage", res?.message || t("auth.forgot-password.resetLinkMessage", "Reset password link sent to email.")));
      } else {
        notify("error", t(res?.messageKey || res?.error || "toast.somethingWentWrong", res?.message || t("toast.somethingWentWrong", "Something went wrong while creating the listing")));
      }
    } catch (err) {
      console.error(err);
      notify("error", t("toast.unexpectedError", "Unexpected error occurred."));
    } finally {
      setStatusLoadingId(null);
    }
  };

  const handleUpdateUserOpen = (user) => {
    setSelectedUser(user);
    setUpdateUserOpen(true);
  };

  const handleUpdateUserClose = () => {
    setUpdateUserOpen(false);
    setSelectedUser(null);
  };

  const handleToggleAccount = async (
    row,
    isActive,
    reasonText = null,
    selectedReason = null,
    onSuccess
  ) => {
    const newStatus = isActive ? "active" : "suspended";

    setStatusLoadingId(row._id);

    try {
      const res = await userServices.updateUserDetails(row._id, {
        accountStatus: newStatus,
        adminRemarks: reasonText,
        selectedReason,
      });

      const dataObj = res?.data?.data || res?.data || res;
      if (res?.data?.success || dataObj?._id) {
        notify("success", t(res?.data?.messageKey || "page.users.toast.statusUpdated", res?.data?.message || t("page.users.toast.statusUpdated", "Status updated successfully")));

        setItems((prev) =>
          prev.map((u) =>
            u._id === row._id ? { ...u, accountStatus: newStatus } : u
          )
        );

        if (onSuccess) onSuccess();
      } else {
        notify("error", t(res?.data?.messageKey || res?.data?.error || "page.users.toast.somethingWentWrong", res?.data?.message || t("page.users.toast.somethingWentWrong", "Something went wrong")));
      }
    } catch (err) {
      console.error(err);
      notify("error", t("page.users.toast.unexpectedError", "Unexpected error occurred."));
    } finally {
      setStatusLoadingId(null);
    }
  };

  const updateUser = async (updatedData) => {
    if (!selectedUser) return;

    try {
      const parts = updatedData.name.trim().split(" ");
      const payload = {
        firstName: parts[0],
        lastName: parts.slice(1).join(" "),
        email: updatedData.email?.trim().toLowerCase(),
        phoneNumber: updatedData.phoneNumber,
        country: updatedData.country,
        homeAddress: updatedData.homeAddress,
        uploadedIdType: updatedData.uploadedIdType,
        uploadedIdNumber: updatedData.uploadedIdNumber,
      };

      const res = await userServices.updateUserDetails(
        selectedUser._id,
        payload
      );

      const resBody = res?.data || res;
      const dataObj = resBody?.data || resBody;

      if (resBody?.error) {
        const errorKey = resBody.error;
        if (errorKey === "auth.signup.emailAlreadyUsed") {
          return { error: errorKey };
        }

        notify("error", t(resBody.messageKey || errorKey, resBody.message || t("toast.unexpectedError", "Unexpected error occurred.")));
        return { error: errorKey };
      }

      if (resBody?.success || dataObj?._id) {
        notify("success", t(resBody?.messageKey || "page.users.toast.userUpdated", resBody?.message || t("page.users.toast.userUpdated", "User updated successfully")));
        setItems((prev) =>
          prev.map((u) =>
            u._id === selectedUser._id ? { ...u, ...updatedData } : u
          )
        );
        handleUpdateUserClose();
        return { success: true };
      }

      return { error: "toast.unexpectedError" };
    } catch (err) {
      console.error(err);
      notify("error", t("toast.unexpectedError", "Unexpected error occurred."));
      return { error: "toast.unexpectedError" };
    }
  };

  const userActions = createUserActionHandlers({
    onDelete: deleteRow,
    onResetPassword: updatePassword,
    onEditUser: handleUpdateUserOpen,
    onViewUser: (user) =>
      navigate(`/admin/users/userDetails/${user._id}`),
  });

  return (
    <div className="card-header-full-height">
      <SimpleHeader name={t("page.users.title", "Users")} parentName={t("sideNav.users", "Users")} />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <UsersTable
                userActions={userActions}
                deleteRow={deleteRow}
                updatePassword={updatePassword}
                updateUser={handleUpdateUserOpen}
                items={items}
                setItems={setItems}
                handleToggleAccount={handleToggleAccount}
                onTableChange={handleTableChange}
                tableState={tableState}
                setTableState={setTableState}
                meta={meta}
                filterType={filterType}
                setFilterType={setFilterType}
                statusLoadingId={statusLoadingId}
                setStatusLoadingId={setStatusLoadingId}
                isItemsLoading={isItemsLoading}
              />
            </Card>
          </div>
        </Row>
      </Container>

      <DeleteModal
        isOpen={openModal}
        toggle={handleModalClose}
        itemName="user"
        onDelete={deleteUser}
        loading={statusLoadingId !== null}
      />

      <UpdateUserModal
        user={selectedUser}
        open={updateUserOpen}
        onClose={handleUpdateUserClose}
        onSave={updateUser}
      />
    </div>
  );
};

export default UsersPage;
